/* eslint-disable @typescript-eslint/no-explicit-any */
import Bowser from 'bowser';
import { FormikErrors, FormikTouched } from 'formik';

import { Country } from '../types/global';

import { currencyCodeMap } from 'common/config/constants/currency-code';

import { AstroCustomHeaders } from './cookiesHandler';
import { camelCase, snakeCase } from './string';

export const countryToFlag = (isoCode: string) => {
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    : isoCode;
};

export const isObject = (value: unknown): boolean => {
  const type = typeof value;
  return value != null && (type === 'object' || type === 'function');
};

export const getRandomItem = <T>(array: T[]) => {
  return array[Math.floor(Math.random() * array.length)];
};

/**
 * Returns error message of validation
 * @param fieldName
 * @param errors
 * @param touched
 * @returns {string} Returns error message of validation
 */
export const errorMessageByField = <T>(
  fieldName: string,
  touched: any = {} as FormikTouched<T>,
  errors: any = {} as FormikErrors<T>,
) => {
  if (fieldName.indexOf('.') > -1) {
    const splittedName = fieldName.split('.');
    const touchedObject = touched[splittedName[0] as keyof T];
    const errorsObject = splittedName[0] in errors && errors[splittedName[0] as keyof T];
    if (errorsObject && touchedObject) {
      const errorMessage =
        (touched[splittedName[0]][splittedName[1]] as boolean) &&
        errors[splittedName[0]][splittedName[1]];
      return errorMessage;
    }
  } else {
    return fieldName in touched && fieldName in errors && (errors[fieldName as keyof T] as string);
  }
};

export const decimalAdjust = (value: number, decimal = 2) => {
  let positiveValue = value;
  if (value < 0) {
    positiveValue = value * -1;
  }
  const multiplier = Math.pow(10, decimal);
  const adjustedValue = Math.round(positiveValue * multiplier);
  const decimals = (adjustedValue % multiplier) / multiplier;
  if (decimals > 0) {
    return parseFloat((adjustedValue / multiplier).toFixed(decimal));
  }
  return parseFloat(Number(value).toFixed(decimal));
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const toCamelCaseObject = <T1 extends {}, T2 = {}>(
  obj: T2,
  indexs: { [key: string]: keyof T1 } = {},
): T1 =>
  Object.entries(obj).reduce(
    (u, [attr, value]) => ({
      ...u,
      [attr in indexs ? indexs[attr] : camelCase(attr)]:
        isObject(value) && !Array.isArray(value)
          ? toCamelCaseObject<any>(value, indexs)
          : Array.isArray(value)
          ? value.map((v) => (isObject(v) ? toCamelCaseObject(v, indexs) : v))
          : value,
    }),
    {} as T1,
  );

export const currencyFormat = (value?: number, decimal = 2) => {
  const _value = value ? (value < 0.01 ? 0 : value) : 0;
  return localizeFormat(decimalAdjust(_value, decimal));
};

export const currencyCodeToSymbol = (input?: string | number): string => {
  if (input === undefined || input === null) {
    return '';
  } else {
    const map = currencyCodeMap;
    let result = input?.toString();
    for (const code in map) {
      const re = new RegExp('\\b' + code + '\\b', 'gi');
      result = result.replace(re, map[code]);
    }

    return result;
  }
};

export const localizeFormat = (value: number, maximumFractionDigits = 2) => {
  try {
    const _value = parseFloat(value.toString());
    const languageIso = navigator.language.split('-')[0];
    const result = _value.toLocaleString(languageIso, {
      maximumFractionDigits: maximumFractionDigits,
    });
    return result;
  } catch {
    return value;
  }
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const toSnakeCaseObject = <T1 extends {}, T2 = {}>(
  obj: T2,
  indexs: { [key: string]: keyof T1 } = {},
): T1 =>
  Object.entries(obj).reduce(
    (u, [attr, value]) => ({
      ...u,
      [attr in indexs ? indexs[attr] : snakeCase(attr)]:
        isObject(value) && !Array.isArray(value)
          ? toSnakeCaseObject<any>(value, indexs)
          : Array.isArray(value)
          ? value.map((v) => (isObject(v) ? toSnakeCaseObject(v, indexs) : v))
          : value,
    }),
    {} as T1,
  );

export const extractFromObj = <T, K extends keyof T>(obj: T, keys: K[], autocomplete = true) =>
  keys.reduce(
    (newObj, key) =>
      key in obj
        ? { ...newObj, [key]: obj[key] }
        : autocomplete
        ? { ...newObj, [key]: '' }
        : newObj,
    {} as Pick<T, K>,
  );

export const inPercent = (percent: number) => {
  return Math.floor(Math.random() * 100) + 1 < percent;
};

export const getCountryFromIso = (countries: Country[], iso: string) => {
  return countries.find((country) => country.iso === iso);
};

export enum OS_SYSTEM {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  MACOS = 'MACOS',
  WINDOWS = 'WINDOWS',
  UNKNOWN = 'UNKNOWN',
}

export const getOperatingSystem = () => {
  const bowser = Bowser.getParser(window.navigator.userAgent);
  const os = bowser.getOSName().toUpperCase();

  if (os && Object.values(OS_SYSTEM).includes(os as OS_SYSTEM)) {
    return os as OS_SYSTEM;
  }

  return OS_SYSTEM.UNKNOWN;
};

export const getMobileOperatingSystem = () => {
  const os = getOperatingSystem();

  if (os === OS_SYSTEM.ANDROID || os === OS_SYSTEM.IOS) {
    return os;
  }

  return OS_SYSTEM.UNKNOWN;
};

export const getUserBrowser = (): { name?: string; version?: string } => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  return browser.getBrowser() ?? undefined;
};

const baseGet = (object: Record<string, any>, path: string) => {
  const paths = path.split('.');
  let index = 0;
  const length = paths.length;

  while (object != null && index < length) {
    object = object[paths[index++]];
  }
  return index && index === length ? object : undefined;
};

export const get = (object: Record<string, any>, path: string, defaultValue?: '') => {
  const result = object === null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
};

export const extractCustomHeaders = (
  response: any,
  sessionCookies?: Record<AstroCustomHeaders, string>,
): Record<AstroCustomHeaders, string> => {
  const headers = response.headers ?? {};
  const customHeaders: Record<AstroCustomHeaders, string> = {} as Record<
    AstroCustomHeaders,
    string
  >;
  Object.values(AstroCustomHeaders)?.map((header: string) => {
    const value = headers[header];

    if (value) {
      customHeaders[header as AstroCustomHeaders] = value;
    }
  });

  return sessionCookies ? { ...sessionCookies, ...customHeaders } : customHeaders;
};
